'use client';
import React, { ReactElement } from 'react';
import styled from '@benzinga/themetron';
import { SearchBar, SearchBarForwardRef, TokenizedExpression, SearchTool } from '../SearchBar';
import { SearchModule } from '../Modules';
import { SearchItem } from '@benzinga/search-modules';
import { TooltipButton } from '@benzinga/widget-tools';
import { PermissionPrompt } from '@benzinga/user-context';
import { SmallSearch } from '@benzinga/themed-icons';

export interface TagSearchProps {
  className?: string;
  flightMode?: boolean;
  iconOverride?: React.ReactNode;
  tags?: SearchItem[];
  placeholder: string;
  thinUI?: boolean;
  onDropDownStateChange?: (isOpen: boolean) => void;
  onTagsUpdated?: (tags: SearchItem[]) => void;
  tools?: SearchTool[];
  modules: SearchModule[];
  newsConfigSelector?: React.ElementType | null;
}

const expressionFromTag = (tag: SearchItem): TokenizedExpression => ({
  ...tag,
  operator: 'expression',
});
const expressionFromTags = (tags: SearchItem[]) => tags.map(expressionFromTag);
const tagFromExpression = (expr: TokenizedExpression): SearchItem | undefined => {
  if (expr.operator === 'expression') {
    delete ({ ...expr } as SearchItem)['operator'];
    return expr;
  }
  return undefined;
};

const tagsFromExpression = (expr: TokenizedExpression[]) =>
  expr.flatMap(e => {
    const tag = tagFromExpression(e);
    return tag != null ? [tag] : [];
  });

export const TagSearch = React.forwardRef<SearchBarForwardRef, TagSearchProps>(
  (props: TagSearchProps, ref: React.ForwardedRef<SearchBarForwardRef>): ReactElement => {
    const onTagsUpdated = React.useCallback(
      (tags: SearchItem[]) => {
        const onTagsUpdated = props.onTagsUpdated;
        onTagsUpdated?.(tags);
      },
      [props.onTagsUpdated],
    );
    const [showUpSellModal, setShowUpSellModal] = React.useState(false);
    const onExpressionUpdate = React.useCallback(
      (expr: TokenizedExpression[]) => {
        const updatedTags = tagsFromExpression(expr);
        onTagsUpdated(updatedTags);
      },
      [onTagsUpdated],
    );
    const expression = React.useMemo(() => expressionFromTags(props.tags ?? []), [props.tags]);
    const onLockedItemsSelected = React.useCallback(() => {
      setShowUpSellModal(true);
    }, []);
    const onCloseModal = () => {
      setShowUpSellModal(false);
    };
    const SearchDiv = props.thinUI ? ThinTagSearchStyler : TagSearchDiv;
    const iconOverride = React.useMemo(
      () =>
        props.iconOverride ?? (
          <TooltipButton className={`TUTORIAL_WidgetContainer-Settings`}>
            <PaddingDiv>
              <SmallSearch />
            </PaddingDiv>
          </TooltipButton>
        ),
      [props.iconOverride],
    );

    return (
      <SearchDiv className={props.className}>
        {showUpSellModal && <PermissionPrompt onClose={onCloseModal} showUpsell />}
        <SearchBar
          displayOr={false}
          excludeAnd={true}
          expression={expression}
          flightMode={props.flightMode}
          iconOverride={iconOverride}
          modules={props.modules}
          newsConfigSelector={props?.newsConfigSelector}
          onDropDownStateChange={props.onDropDownStateChange}
          onExpressionUpdate={onExpressionUpdate}
          onLockedItemsSelected={onLockedItemsSelected}
          placeholder={props.placeholder}
          ref={ref}
          thinUI={props.thinUI}
          tools={props.tools}
        />
      </SearchDiv>
    );
  },
);

export const TagSearchDiv = styled.div`
  overflow-x: hidden;
  width: 100%;
  /* line-height: 30px;
  padding: 2px 5px;
  font-size: 14px;
  align-items: center; */

  position: relative;
  border: 0.01em solid ${props => props.theme.colors.borderDark};

  display: grid;
  grid-template-columns: 1fr;
`;
const ThinTagSearchStyler = styled(TagSearchDiv)`
  /* margin-right: 5px; */
  /* min-width: 250px; */
  width: auto;
  /* overflow: hidden; */
  background-color: ${props => props.theme.colors.background};

  ${TagSearchDiv} {
    margin: 2px;
    border: 0.01em solid ${props => props.theme.colors.borderDark};
    background-color: ${props => props.theme.colors.background};
    min-width: auto;
    max-height: 28px;
  }
`;

const PaddingDiv = styled.div`
  padding: 0 8px;
  ${SmallSearch} {
    fill: ${props => props.theme.colors.accent};
  }
`;
